/**
 * FAQ Content
 */
// tslint:disable:max-line-length

import * as React from 'react';

import Button from '../../atoms/buttons/Button';
import Heading from '../../atoms/text/Heading';
import { Paragraph } from '../../atoms/text/Label';
import { IFAQCategory } from './';

export const FAQContent: IFAQCategory[] = [
  {
    id: 'bopper-vs-royalty-free',
    title: 'Bopper vs. Royalty-free',
    questions: [
      {
        id: 'is-bopper-another-royalty-free-music-directory?',
        question: 'Is Bopper another royalty-free music directory?',
        answer: (
          <>
            <Paragraph>
            No. Although you can buy the rights for a song as quickly and easily on Bopper, the majority of our catalog comes from recording artists whose music is of exceptional quality. In addition, our platform is built to meet the specific needs of brand content producers: Creatives, Producers, Video Editors and Music Supervisors, whether in Ad agencies, Trailer houses, TV productions, etc.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'what-do-you-offer-different-to-producers-of-brand-content?',
        question: 'What do you offer different to producers of brand content?',
        answer: (
          <>
            <Paragraph>
            The ability to license on-brief music from recording artists instantly. First, we remove all obstacles that prevent content producers from quickly licensing real music as an alternative to stock music or when deadlines for custom music are too tight. All tracks are pre-cleared. Secondly,  it’s all about speeding-up the process. Our search engine was created specifically based on our clients’ unique needs and the entire catalogue is curated to make sure all tracks are ad-friendly.
            </Paragraph>
            <Paragraph>We also offer a free music search service and take care of licensing songs from top artists outside of our catalogue.</Paragraph>
          </>
        ),
      },
    ],
  },
  {
    id: 'licensing-music',
    title: 'Licensing Music',
    questions: [
      {
        id: 'sample-license',
        question: 'Can I see the license agreement?',
        answer: (
          <>
            <div>
              <Button
                className="sample-license-button"
                hyperLinkProps={{
                  href: 'https://assets.boppermusic.com/docs/Bopper_Sample_License.pdf',
                  target: '_blank',
                }}
                wrapperComponent="a"
                colorScheme="light"
                variant="pill"
                size="small"
                label="Preview License"
              />
            </div>
          </>
        ),
      },
      {
        id: 'how-territory-affects-pricing',
        question: 'How territory affects pricing: Broadcast territory vs. Company location',
        answer: (
          <>
            <Paragraph>
              When using our License Builder, some media options (tv, theatrical, radio) require you to select a broadcast territory. This information is used by our pricing algorithm to determine the price of a license. Other media settings, for instance, Paid Social, require that you select the location of your office. This is to account that it is generally tedious to track broadcast territories of digital media. The algorithm will then use the Licensee’s office location to evaluate the cost of a license.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'how-do-i-set-the-territory-parameter',
        question: 'My content is going on the web, how do I set the territory parameter?',
        answer: (
          <>
            <Paragraph>
              All Web Streaming and Paid Social licenses on Bopper are for worldwide use. The price of a license will still vary according to the production company’s office location. For example, a Web Streaming license for an ad agency based in Brazil will be less expensive than the same license for an agency based in the United States.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'payment-methods',
        question: 'Payment methods',
        answer: (
          <>
            <Paragraph>
              We accept payments most major credit cards (Visa, Mastercard, AMEX), and also bank transfers and check payment within 30 days of purchasing a license. Our bank transfer info is at the bottom of our invoices. <a href="mailto:everything@boppermusic.com">Email us</a> if you can’t find it.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'renewing-a-license',
        question: 'How do I renew a license?',
        answer: (
          <>
            <Paragraph>
              To renew a license for an additional period of time, simply purchase it again by setting your license terms, adding the track to your cart and checking out again. Some music might be removed from our platform from time to time. Even if that music cannot be found on the platform, you can renew a license for any consecutive term, by <a href="mailto:everything@boppermusic.com">contacting us</a>.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'regional-or-national-campaign',
        question: 'Is my campaign Regional or National?',
        answer: (
          <>
            <Paragraph>
              If you are licensing music in the United States you will need to select whether your campaign is a National (all states), or a Regional campaign. Regional is defined herein as the broadcast of content in less than four US Television Market Areas (TMAs).
            </Paragraph>

            <Paragraph>
              A full list of TMAs can be found <a href="https://en.wikipedia.org/wiki/List_of_United_States_television_markets">here</a>.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'license-territories-and-clusters',
        question: 'License Territory Clusters',
        answer: (
          <>
            <Paragraph>Bopper offers many territory clusters that are frequently used by media planners to speak to a language-defined audience. Here is a list of what each cluster includes. Can’t find your perfect cluster? <a href="mailto:everything@boppermusic.com">Drop us a line</a> and we’ll help to create a custom cluster just for you.</Paragraph>
            <Heading is="h6">Baltic States</Heading>
            <Paragraph>The cluster comprises Estonia, Latvia, and Lithuania.</Paragraph>
            <Heading is="h6">Eastern Europe</Heading>
            <Paragraph>Countries included in this region are Estonia, Latvia, Lithuania, Armenia, Azerbaijan, Georgia, Russia, Ukraine, Belarus, Moldova, Austria, Czech Republic, Hungary, Poland, Slovakia, Slovenia, Albania, Bosnia & Herzegovina, Bulgaria, Croatia, Cyprus, Greece, Macedonia, Montenegro, Romania, Serbia and Turkey.</Paragraph>
            <Heading is="h6">GAS - DACH</Heading>
            <Paragraph>This cluster consists of Germany, Austria, and Switzerland.</Paragraph>
            <Heading is="h6">Greater Chinese Region (GCR)</Heading>
            <Paragraph>GCR (Greater Chinese Region) is comprised of Mainland China, Hong Kong, Macao, and Taiwan.</Paragraph>
            <Heading is="h6">Latin America and Caribean</Heading>
            <Paragraph>This cluster includes all Latin American and Caribean countries and territories, namely Anguilla, Antigua & Barbuda, Argentina, Aruba, Bahamas, Barbados, Belize, Bolivia, Brazil, Cayman Islands, Chile, Colombia, Costa Rica, Cuba, Curacao, Dominican Republic, Ecuador, En Cifras, El Salvador, Guatemala, Haiti, Honduras, Jamaica, Mexico, Montserrat, Nicaragua, Panama, Paraguay, Peru, Puerto Rico, Abre, Sint Maarten, St. Lucia, St. Vincent & the Grenadines, Suriname, Trinidad & Tobago, Turks & Caicos Islands, Uruguay, Venezuela, and Venescopio.</Paragraph>
            <Heading is="h6">Middle East and North Africa (MENA)</Heading>
            <Paragraph>Middle East and North Africa is a geographical region consisting of the majority of Middle-Eastern and Maghreb countries. Countries included in the MENA group are Algeria, Armenia, Azerbaijan, Bahrain, Cyprus, Djibouti, Egypt, Georgia, Iran, Iraq, Israel, Jordan, Kuwait, Lebanon, Yemen, Malta, Somalia, Sudan, Turkey, United Arab Emirates, Libya, Morocco, Oman, West Bank & Gaza, Qatar, Saudi Arabia, Syria, Tunisia.</Paragraph>
            <Heading is="h6">Netherlands, SSS, and ABC islands</Heading>
            <Paragraph>The Kingdom of the Netherlands including the Dutch-speaking islands of Aruba, Bonaire and Curaçao, Sint Eustatius, Saba and Sint Maarten.</Paragraph>
            <Heading is="h6">Portugal, Brazil and CPLP</Heading>
            <Paragraph>The Community of Portuguese Language Countries (abbreviated as CPLP) comprises all Lusophone countries and territories. It includes Portugal, Brazil, Angola, Cape Verde, Guinea-Bissau, Mozambique, São Tomé and Príncipe, Timor-Leste, and Equatorial Guinea.</Paragraph>
            <Heading is="h6">Russia and CIS</Heading>
            <Paragraph>Russian Federation (Russia) and all the members of the Commonwealth of Independent States (CIS) namely Armenia, Azerbaijan, Belarus, Georgia, Kazakhstan, Kyrgyzstan, Moldova, Russian Federation, Tajikistan, Turkmenistan, Ukraine, and Uzbekistan.</Paragraph>
            <Heading is="h6">Southeast Asia</Heading>
            <Paragraph>Southeast Asia is a subregion of Asia, consisting of the countries that are geographically located south of China, east of India, west of New Guinea and north of Australia. Countries included in this region are Brunei, Cambodia, East Timor, Indonesia, Laos, Malaysia, Myanmar, Philippines, Singapore, Thailand, Vietnam, Christmas Island and the Cocos Islands.</Paragraph>
            <Heading is="h6">UK and BOTs</Heading>
            <Paragraph>This cluster comprises the United Kingdom (England, Scotland, Wales and Northern Ireland) and the <a href="https://en.wikipedia.org/wiki/British_Overseas_Territories" target="_blank">17 British overseas territories</a>.</Paragraph>
            <Heading is="h6">Western Europe</Heading>
            <Paragraph>Countries included in this region are Austria, Belgium, Denmark, Finland, France, Germany, Iceland, Ireland, Italy, Liechtenstein, Luxembourg, Monaco, Netherlands, Norway, Portugal, Spain, Sweden, Switzerland, the UK.</Paragraph>
          </>
        ),
      },
      {
        id: 'instrumental-versions-cut-downs-edits',
        question: 'Instrumental versions / cut-downs & edits',
        answer: (
          <>
            <Paragraph>
              To see if there is an instrumental version available for a track, simply click on the track, which will open the sidebar. There you will be able to see any instrumental/alternate versions of the track. Keep in mind that not all tracks have instrumental versions available. Once you’ve purchased a license, you will gain access to all existing instrumental versions, alternative versions, cut-downs, etc. for the track(s) that you licensed. If you need custom modifications for any track please <a href="mailto:everything@boppermusic.com">contact us</a>.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'cut-downs-and-language-versions',
        question: 'Cut-downs and language versions',
        answer: (
          <>
            <Paragraph>
              Unlimited cut-downs of a given piece of content are allowed. This includes translations in different languages. A single piece of content that is cut-down in various lengths and translated into many languages counts as one film/spot. If there is different filmed content in your final videos, they count as different spots.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'licensing-a-track',
        question: 'Licensing a track',
        answer: 'Once you’ve selected the track(s) you’d like to license, you can enter the terms of your project in our License Builder to determine the price of your license. You may then add the track(s) to your cart and check out with any major credit card, or you may opt to pay by cheque, in which case you have 30 days to pay for your license. After checking out you will be able to access your license, invoice and the high-res music files. These may be accessed at any time on your Account > My Orders page.',
      },
      {
        id: 'dont-have-an-exact-air-date',
        question: 'I don’t have an exact air-date yet, what should I do?',
        answer: 'If you’re not sure of the exact start date of your campaign, please enter an approximate date within a three-week range of the actual air date.',
      },
      {
        id: 'archival-use',
        question: 'Archival use',
        answer: (
          <>
            <Paragraph>
              All licenses purchased on Bopper include archival rights. These additional rights are added for free to our license, and allow licensees to use the music on all digital media for archival purposes, in perpetuity. Examples of archival uses are unpromoted social media posts, unpromoted Youtube archives, and corporate websites. Whenever content is promoted, either by purchasing media or paying to drive traffic to a website, it is no longer considered an archival use, and an appropriate license must be purchased on Bopper.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'is-the-music-on-bopper-music-store-royalty-free',
        question: 'Is the music on Bopper royalty-free?',
        answer: (
          <>
            <Paragraph>
              “Royalty-free” music refers to a type of music licensing that allows the purchaser to pay for a music license only once and have the rights to use the music in perpetuity, across all media. When licensing music on Bopper, you must specify the term, territory, and media for your production and your license will be valid for those terms. In that sense, the music on Bopper is not royalty-free. Rest assured though, once you purchase a license on Bopper, all the rights have been thoroughly cleared so that you won’t have to pay anything other than your license fee.
            </Paragraph>
          </>
        ),
      },
    ],
  },
  {
    id: 'submit-music',
    title: 'Submitting Music',
    questions: [
      {
        id: 'how-do-i-submit-music-for-consideration',
        question: 'How do I submit music for consideration?',
        answer: (
          <Paragraph>Please send a link (Dropbox, Wetransfer, etc.) to samples of your music to <a href="mailto:music@boppermusic.com">music@boppermusic.com</a>. We listen to everything, but please give us 30 days to get back to you, as we receive an important amount of submissions.</Paragraph>
        ),
      },
      {
        id: 'why-would-recording-artists-distribute-a-song-on-bopper',
        question: 'Why would recording artists distribute a song on Bopper?',
        answer: (
          <Paragraph>One of our values is to help recording artists get fair compensation for their work. Not only do we make it much easier to get their songs in front of brand content producers, but when a track gets chosen, we make sure the artist will get maximum compensation. In other words: Bopper provides equitable music catering to socially responsible brands.</Paragraph>
        ),
      },
      {
        id: 'how-can-we-ensure-that-our-artists-get-maximum-compensation',
        question: 'How can we ensure that our artists get maximum compensation?',
        answer: (
          <Paragraph>Our proprietary pricing algorithm prices each track individually based on 24 different data points. To make it simple we take into account the license terms (market, campaign duration, number of films…) AND the artist’s reputation. Brand content producers can search songs based on the budget available making it a win-win for both parties. Depending on the license terms, compensation can range from a few hundred dollars to a few thousand.</Paragraph>
        ),
      },
    ],
  },
  {
    id: 'other',
    title: 'Other Questions',
    questions: [
      {
        id: 'errors-and-omissions-insurance',
        question: 'What is your errors & omissions insurance coverage?',
        answer: (
          <>
            <Paragraph>
              All licenses obtained on Bopper are fully insured. Our errors and omissions (E&O) policy covers up to 2M$ USD per incident. If need be, we can explicitly name any end-user as a beneficiary. <a href="mailto:everything@boppermusic.com">Contact us</a> for any specific insurance needs.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'is-ams-on-omnicom-musiclink',
        question: 'Is Bopper on Omnicom MusicLink?',
        answer: (
          <>
            <Paragraph>
            Bopper is an approved vendor of MusicLink and the Omnicom Group. Any agency that’s part of the Omnicom Group is therefore authorized to purchase music on Bopper. Most of our music is not posted on the MusicLink platform itself but any track that is displayed on Bopper is available to purchase by Omnicom agencies.  If the specific track that you need to purchase is not listed on MusicLink we can post it on MusicLink and <a href="mailto:everything@boppermusic.com">assist you</a> with licensing it through that platform.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'why-cant-i-see-the-price-for-a-song',
        question: 'Why can’t I see the price for a song after selecting my license terms?',
        answer: (
          <>
            <Paragraph>
              A very small selection of tracks on Bopper cannot be licensed in certain territories. When that’s the case, please reach out to us and we can make sure we provide you with the right license for your needs under these circumstances.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'is-your-music-under-any-union-contracts',
        question: 'Is your music under any union contracts?',
        answer: (
          <>
            <Paragraph>
            The music on Bopper is not recorded under AFofM, SAG, ACTRA, or UDA, or any other union agreement. This is a pre-requisite of many ad agencies around the world when using music libraries, mostly because of the complexity of calculating residual fees.
            </Paragraph>
          </>
        ),
      },
      {
        id: 'didnt-find-what-you-were-looking-for',
        question: 'Didn’t find what you were looking for?',
        answer: (
          <>
            <Paragraph>
              Our team of music supervisors is standing by ready to assist you in finding the perfect piece of music to suit your project. Simply start a chatbox with one of our team members in the chatbox below, email us, or call us toll-free at 1-888-423-6665 (US) / +33 1 70 38 73 50 (EU) and we’ll be happy to help you.
            </Paragraph>
          </>
        ),
      },
    ],
  },
];



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/FAQ/content.tsx